import { __assign } from '../../node_modules/tslib/tslib.es6.mjs.js';
import React, { useRef, useState, useLayoutEffect, useEffect, useCallback } from 'react';
import { useMotionValue, useDragControls, animate, motion } from 'framer-motion';
import BottomSheetModalStyles from './BottomSheetModal.css.js';

var DEFAULT_PEEK_HINT = "Swipe up to edit";
// Inline so it beats the stylesheet's display rules. display:none also drops the
// subtree from the a11y tree and tab order, so collapsed content stays unreachable.
var hiddenWhenCollapsed = { display: "none" };
function usePeekExpandedState(isControlled, controlledValue, defaultPeekExpanded, onPeekExpandedChange) {
    var _a = useState(defaultPeekExpanded), internal = _a[0], setInternal = _a[1];
    var expanded = isControlled ? Boolean(controlledValue) : internal;
    var setExpanded = useCallback(function (next) {
        onPeekExpandedChange === null || onPeekExpandedChange === void 0 ? void 0 : onPeekExpandedChange(next);
        if (!isControlled)
            setInternal(next);
    }, [isControlled, onPeekExpandedChange]);
    return [expanded, setExpanded];
}
function CloseIcon() {
    return (React.createElement("svg", { className: "bottom-sheet-modal__close-icon", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", "aria-hidden": true },
        React.createElement("path", { d: "M18 6L6 18M6 6l12 12" })));
}
var BottomSheetModal = function (_a) {
    var _b, _c;
    var children = _a.children, header = _a.header, footer = _a.footer, _d = _a.peekTitle, peekTitle = _d === void 0 ? "" : _d, _e = _a.peekHint, peekHint = _e === void 0 ? DEFAULT_PEEK_HINT : _e, _f = _a.maxHeight, maxHeight = _f === void 0 ? "92vh" : _f, _g = _a.fitContent, fitContent = _g === void 0 ? false : _g, _h = _a.keepMounted, keepMounted = _h === void 0 ? false : _h, _j = _a.peekHeightPx, peekHeightPx = _j === void 0 ? 88 : _j, _k = _a.showDragHandle, showDragHandle = _k === void 0 ? true : _k, _l = _a.hideCloseButton, hideCloseButton = _l === void 0 ? false : _l, _m = _a.direction, direction = _m === void 0 ? "ltr" : _m, _o = _a.styles, styles = _o === void 0 ? {} : _o, isPeekExpanded = _a.isPeekExpanded, _p = _a.defaultPeekExpanded, defaultPeekExpanded = _p === void 0 ? false : _p, onPeekExpandedChange = _a.onPeekExpandedChange, onClose = _a.onClose, _q = _a.closeOnOverlayClick, closeOnOverlayClick = _q === void 0 ? true : _q, zIndex = _a.zIndex, _r = _a.resetExpandedOnClose, resetExpandedOnClose = _r === void 0 ? true : _r;
    var overlayZ = (_b = zIndex === null || zIndex === void 0 ? void 0 : zIndex.overlay) !== null && _b !== void 0 ? _b : 1400;
    var sheetZ = (_c = zIndex === null || zIndex === void 0 ? void 0 : zIndex.sheet) !== null && _c !== void 0 ? _c : 1410;
    var isControlled = isPeekExpanded !== undefined;
    var _s = usePeekExpandedState(isControlled, isPeekExpanded, defaultPeekExpanded, onPeekExpandedChange), expanded = _s[0], setExpanded = _s[1];
    var sheetRef = useRef(null);
    var _t = useState(0), collapsedY = _t[0], setCollapsedY = _t[1];
    var y = useMotionValue(0);
    var dragControls = useDragControls();
    var ready = collapsedY > 0;
    useLayoutEffect(function () {
        var el = sheetRef.current;
        if (!el)
            return;
        var measure = function () {
            var h = el.offsetHeight;
            setCollapsedY(Math.max(0, h - peekHeightPx));
        };
        measure();
        var ro = new ResizeObserver(measure);
        ro.observe(el);
        return function () { return ro.disconnect(); };
    }, [maxHeight, peekHeightPx, expanded, fitContent]);
    useEffect(function () {
        if (!ready)
            return;
        animate(y, expanded ? 0 : collapsedY, {
            type: "spring",
            stiffness: 420,
            damping: 40,
        });
    }, [expanded, collapsedY, ready, y]);
    var startPeekDrag = useCallback(function (event) {
        dragControls.start(event.nativeEvent, { snapToCursor: false });
    }, [dragControls]);
    var collapse = useCallback(function () {
        if (resetExpandedOnClose) {
            setExpanded(false);
        }
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [setExpanded, resetExpandedOnClose, onClose]);
    var handleDragEnd = useCallback(function (_, info) {
        if (!ready)
            return;
        var velocity = info.velocity, offset = info.offset;
        var absX = Math.abs(offset.x);
        var absY = Math.abs(offset.y);
        var horizontalDominant = absX > absY * 1.25 ||
            (Math.abs(velocity.x) > Math.abs(velocity.y) &&
                Math.abs(velocity.x) > 0.35);
        if (horizontalDominant) {
            animate(y, expanded ? 0 : collapsedY, {
                type: "spring",
                stiffness: 420,
                damping: 40,
            });
            return;
        }
        var threshold = collapsedY * 0.2;
        var next = expanded;
        if (velocity.y < -400 || offset.y < -threshold) {
            next = true;
        }
        else if (velocity.y > 400 || offset.y > threshold) {
            next = false;
        }
        else {
            var current = y.get();
            next = current < collapsedY / 2;
        }
        setExpanded(next);
        animate(y, next ? 0 : collapsedY, {
            type: "spring",
            stiffness: 420,
            damping: 40,
        });
    }, [ready, collapsedY, expanded, y, setExpanded]);
    var peekInnerClass = "bottom-sheet-modal__peek-inner" +
        (direction === "rtl" ? " bottom-sheet-modal__peek-inner--rtl" : "");
    var peekRow = (React.createElement("div", { className: "bottom-sheet-modal__peek-row", onPointerDown: startPeekDrag },
        showDragHandle && (React.createElement("div", { className: "bottom-sheet-modal__handle-row" },
            React.createElement("div", { className: "bottom-sheet-modal__handle-pill", "aria-hidden": true }))),
        React.createElement("div", { className: peekTitle || (peekHint && !expanded) ? peekInnerClass : '' },
            peekTitle != null && peekTitle !== "" ? (React.createElement("p", { className: "bottom-sheet-modal__title" }, peekTitle)) : (React.createElement("span", { className: "bottom-sheet-modal__title-spacer", "aria-hidden": true })),
            peekHint != null && !expanded ? (React.createElement("p", { className: "bottom-sheet-modal__hint" }, peekHint)) : null),
        peekTitle || (peekHint && !expanded) ? React.createElement("div", { className: "bottom-sheet-modal__peek-divider", role: "separator", "aria-hidden": true }) : null));
    var ariaLabel = typeof peekTitle === "string" && peekTitle !== ""
        ? peekTitle
        : "Bottom sheet";
    var useContentHeight = fitContent && expanded;
    var sheetClassName = "bottom-sheet-modal__sheet" +
        (useContentHeight ? " bottom-sheet-modal__sheet--fit-content" : "");
    var contentClassName = "bottom-sheet-modal__content" +
        (useContentHeight ? " bottom-sheet-modal__content--fit-content" : "");
    var bodyClassName = "bottom-sheet-modal__body" +
        (useContentHeight ? " bottom-sheet-modal__body--fit-content" : "");
    return (React.createElement(React.Fragment, null,
        React.createElement("style", null, BottomSheetModalStyles.styles),
        expanded && (React.createElement(motion.div, { className: "bottom-sheet-modal__overlay", initial: { opacity: 0 }, animate: { opacity: 0.48 }, exit: { opacity: 0 }, transition: { duration: 0.2 }, style: {
                zIndex: overlayZ,
                pointerEvents: closeOnOverlayClick ? "auto" : "none",
            }, onClick: closeOnOverlayClick ? collapse : undefined, "aria-hidden": true })),
        React.createElement(motion.div, { ref: sheetRef, className: sheetClassName, style: __assign(__assign({ zIndex: sheetZ, maxHeight: maxHeight }, (useContentHeight
                ? {
                    height: "auto",
                    "--bottom-sheet-max-h": maxHeight,
                }
                : { height: maxHeight })), { y: y, visibility: ready ? "visible" : "hidden" }), drag: "y", dragDirectionLock: true, dragControls: dragControls, dragListener: false, dragConstraints: { top: 0, bottom: collapsedY }, dragElastic: { top: 0.05, bottom: 0.05 }, onDragEnd: handleDragEnd, role: "dialog", "aria-modal": expanded, "aria-label": ariaLabel },
            React.createElement("div", { className: contentClassName, style: styles.content },
                peekRow,
                !hideCloseButton && expanded && (React.createElement("div", { className: "bottom-sheet-modal__close-wrap " +
                        (direction === "rtl"
                            ? "bottom-sheet-modal__close-wrap--rtl"
                            : "bottom-sheet-modal__close-wrap--ltr"), onPointerDown: function (e) { return e.stopPropagation(); } },
                    React.createElement("button", { type: "button", className: "bottom-sheet-modal__close", onClick: collapse, "aria-label": "Close" },
                        React.createElement(CloseIcon, null)))),
                (expanded || keepMounted) &&
                    (React.createElement(React.Fragment, null,
                        header ? (React.createElement("header", { className: "bottom-sheet-modal__header", style: __assign(__assign({}, styles.header), (expanded ? null : hiddenWhenCollapsed)) }, header)) : null,
                        React.createElement("div", { className: bodyClassName, style: __assign(__assign({}, styles.body), (expanded ? null : hiddenWhenCollapsed)) }, children),
                        footer ? (React.createElement("footer", { className: "bottom-sheet-modal__footer", style: __assign(__assign({}, styles.footer), (expanded ? null : hiddenWhenCollapsed)) }, footer)) : null))))));
};

export { BottomSheetModal as default };
//# sourceMappingURL=index.js.map
